import {
  Datepicker,
  MoneyInput,
  NumberInput,
  Select,
  Textarea,
} from "@src/components/elements";
import { RadioGroup } from "@src/components/elements/RadioGroup";
import { IntakeSubmissionOptionSelect } from "@src/components/elements/Select/IntakeSubmissionOptionSelect";
import { DocumentManager } from "@src/components/modules";
import { AsyncMemberSelect } from "@src/components/modules/AsyncSelect/AsyncMemberSelect";
import { VendorPortalDocumentManager } from "@src/components/modules/DocumentManager/VendorPortalDocumentManager";
import { useSession } from "@src/contexts/SessionContext";
import {
  useSudoziQuery,
  useSudoziVendorQuery,
} from "@src/infrastructure/useSudoziQuery";
import { IntakeAddressField } from "@src/pages/settings/intake-requests/edit-intake-category/IntakeAddressField";
import { GetGlobalDocumentsFromIdsRoute } from "@src/sudozi-schemas/api-route-schemas";
import { currencyOptions } from "@src/sudozi-schemas/currencies";
import {
  CreateIntakeSubmissionSchema,
  IntakeQuestionType,
} from "@src/sudozi-schemas/intake-submission-schemas";
import React from "react";
import { Control, Controller, DeepMap, FieldError } from "react-hook-form";
import { match } from "ts-pattern";

export function validateCustomField(
  field: CreateIntakeSubmissionSchema["steps"][number]["fields"][number],
): boolean {
  if (field.value && field.type === IntakeQuestionType.EMAIL) {
    return /\S+@\S+\.\S+/.test(field.value);
  }

  if (
    field.type === IntakeQuestionType.NEEDS_REQUEST_SIGNER ||
    field.type === IntakeQuestionType.DISPLAY_ONLY
  )
    return true;

  if (field.isRequired && field.type === IntakeQuestionType.ADDRESS) {
    return (
      !!field.value.address1.trim() &&
      !!field.value.city.trim() &&
      !!field.value.state.trim() &&
      !!field.value.postalCode.trim() &&
      !!field.value.country.trim()
    );
  }

  if (field.isRequired && field.type === IntakeQuestionType.YES_NO) {
    return field.value !== null;
  }

  if (field.isRequired && field.type === IntakeQuestionType.MONEY) {
    return field.value !== null;
  }

  if (field.isRequired && field.type === IntakeQuestionType.REQUEST_AMOUNT) {
    return field.value !== null;
  }

  if (field.isRequired && field.type === IntakeQuestionType.NUMBER) {
    return field.value !== null;
  }

  if (field.isRequired && field.type === IntakeQuestionType.DURATION) {
    return !!field.value?.years || !!field.value?.months || !!field.value?.days;
  }

  if (field.type === "DEPARTMENT_SELECT_V2") {
    if (
      field.isMulti &&
      field.requireDepartmentApproverAssigneeSelection &&
      field.value.some((v) => !v.departmentApproverAssigneeUserId)
    ) {
      return false;
    }
    if (
      !field.isMulti &&
      field.requireDepartmentApproverAssigneeSelection &&
      !!field.value &&
      !field.value.departmentApproverAssigneeUserId
    ) {
      return false;
    }
  }

  if (field.isRequired) {
    return Array.isArray(field.value) ? field.value.length > 0 : !!field.value;
  }

  return true;
}

export const useExistingDocs = ({
  forVendorPortal,
  field,
  forCompanyId,
}: {
  forVendorPortal: boolean;
  field: CreateIntakeSubmissionSchema["steps"][number]["fields"][number];
  forCompanyId?: string;
}) => {
  const docId = field.type === IntakeQuestionType.DOCUMENTS ? field.value : [];

  const { data: docs } = useSudoziQuery({
    route: GetGlobalDocumentsFromIdsRoute,
    query: {
      documentId: docId,
      companyId: forCompanyId ?? undefined,
    },
    queryKey: [
      "GET /api/global/get-documents-from-ids",
      field.questionId,
      field.value,
    ],
    options: {
      enabled: forVendorPortal
        ? false
        : field.type === IntakeQuestionType.DOCUMENTS && field.value.length > 0,
      cacheTime: 0,
    },
  });

  const { data: vendorDocs } = useSudoziVendorQuery({
    route: GetGlobalDocumentsFromIdsRoute,
    query: {
      documentId: docId,
      companyId: forCompanyId ?? undefined,
    },
    queryKey: ["GET /api/global/get-documents-from-ids", field.questionId],
    options: {
      enabled:
        !!forCompanyId &&
        forVendorPortal &&
        field.type === IntakeQuestionType.DOCUMENTS &&
        field.value.length > 0,
      cacheTime: 0,
    },
  });

  const existingDocs =
    docs && docs.length
      ? docs
      : vendorDocs && vendorDocs.length
      ? vendorDocs
      : [];
  return existingDocs;
};

type Props = {
  stepIndex: number;
  fieldIndex: number;
  control: Control<CreateIntakeSubmissionSchema>;
  shouldShow: boolean;
  field: CreateIntakeSubmissionSchema["steps"][number]["fields"][number];
  errors: DeepMap<CreateIntakeSubmissionSchema, FieldError>;
  disabled?: boolean;
  skipValidation?: boolean;
} & (
  | {
      forVendorPortal?: never;
      forCompanyId?: never;
    }
  | {
      forVendorPortal: true;
      forCompanyId: string;
    }
);

export const IntakeFormInput: React.FC<Props> = ({
  stepIndex,
  fieldIndex,
  control,
  shouldShow,
  field: questionField,
  errors,
  forCompanyId,
  forVendorPortal,
  disabled = false,
  skipValidation,
}) => {
  return (
    <Controller
      name={`steps.${stepIndex}.fields.${fieldIndex}`}
      control={control}
      defaultValue={questionField}
      rules={
        skipValidation
          ? undefined
          : {
              validate: (v) => {
                if (!shouldShow) return true;
                return validateCustomField(v);
              },
            }
      }
      render={({ field }) => {
        return shouldShow ? (
          <CustomFormInput
            field={field.value}
            hasError={!!errors?.steps?.[stepIndex]?.fields?.[fieldIndex]}
            name={`steps.${stepIndex}.fields.${fieldIndex}`}
            onChange={field.onChange}
            disabled={disabled}
            {...(forVendorPortal
              ? { forVendorPortal: true, forCompanyId }
              : {
                  forCompanyId: undefined,
                  forVendorPortal: undefined,
                })}
          />
        ) : (
          <></>
        );
      }}
    />
  );
};

export const CustomFormInput = ({
  field,
  name,
  hasError,
  onChange,
  forCompanyId,
  disabled = false,
}: {
  field: CreateIntakeSubmissionSchema["steps"][number]["fields"][number];
  name: string;
  hasError: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (...args: any[]) => void;
  disabled?: boolean;
} & (
  | {
      forVendorPortal?: never;
      forCompanyId?: never;
    }
  | {
      forVendorPortal: true;
      forCompanyId: string;
    }
)) => {
  const { enableCurrencies, vendorUser } = useSession();
  const existingDocs = useExistingDocs({
    forVendorPortal: !!vendorUser,
    field,
    forCompanyId,
  });

  if (field.type === IntakeQuestionType.DOCUMENTS && !existingDocs) {
    return null;
  }
  return match(field)
    .with(
      { type: IntakeQuestionType.TEXT },
      { type: IntakeQuestionType.REQUEST_TITLE },
      (v) => (
        <Textarea
          placeholder={field.label}
          value={v.value ?? ""}
          name={name}
          error={hasError ? "Required" : undefined}
          hideError={!hasError}
          onChange={(e) => {
            onChange({
              ...field,
              value: e.target.value,
            });
          }}
          disabled={disabled}
        />
      ),
    )

    .with({ type: IntakeQuestionType.EMAIL }, (v) => (
      <Textarea
        placeholder={field.label}
        value={v.value ?? ""}
        name={name}
        error={hasError ? "Invalid email" : undefined}
        hideError={!hasError}
        onChange={(e) => {
          onChange({
            ...field,
            value: e.target.value,
          });
        }}
        disabled={disabled}
      />
    ))

    .with({ type: IntakeQuestionType.NUMBER }, (v) => (
      <NumberInput
        placeholder={field.label}
        value={v.value}
        name={name}
        error={hasError ? "Required" : undefined}
        setValue={(newValue) => {
          onChange({
            ...field,
            value: newValue,
          });
        }}
        disabled={disabled}
      />
    ))

    .with({ type: IntakeQuestionType.YES_NO }, (v) => (
      <RadioGroup
        error={hasError ? "Required" : undefined}
        label=""
        options={[
          { label: "Yes", value: "1" },
          { label: "No", value: "0" },
        ]}
        selectedItem={
          v.value === true ? "1" : v.value === false ? "0" : undefined
        }
        onChange={(newValue) =>
          onChange({
            ...field,
            value: newValue === "1" ? true : false,
          })
        }
        disabled={disabled}
      />
    ))

    .with(
      { type: IntakeQuestionType.MONEY },
      { type: IntakeQuestionType.REQUEST_AMOUNT },
      (v) => {
        return (
          <div className="row">
            {enableCurrencies && (
              <Select
                isClearable={false}
                options={currencyOptions}
                value={
                  currencyOptions.find((o) => o.value === v.value?.currency) ??
                  currencyOptions[0]
                }
                onChange={(newValue) => {
                  onChange({
                    ...field,
                    value: {
                      ...v.value,
                      currency: newValue?.value ?? currencyOptions[0].value,
                    },
                  });
                }}
                isDisabled={disabled}
                error={hasError ? "Required" : undefined}
              />
            )}

            <MoneyInput
              fullWidth={true}
              placeholder={field.label}
              value={v.value?.value ?? undefined}
              name={name}
              error={hasError ? "Required" : undefined}
              onChange={(newValue) => {
                onChange({
                  ...field,
                  value: {
                    ...v.value,
                    currency: v.value?.currency ?? currencyOptions[0].value,
                    value: newValue,
                  },
                });
              }}
              intlConfig={{
                locale: "en-US",
                currency: v.value?.currency ?? "USD",
              }}
              isDisabled={disabled}
            />
          </div>
        );
      },
    )

    .with(
      { type: IntakeQuestionType.DATE },
      { type: IntakeQuestionType.START_DATE },
      { type: IntakeQuestionType.END_DATE },
      { type: IntakeQuestionType.DUE_DATE },
      (v) => (
        <Datepicker
          hideError={true}
          name={name}
          defaultValue={v.value}
          error={hasError ? "Required" : undefined}
          onChange={(newValue) => {
            onChange({
              ...field,
              value: newValue,
            });
          }}
          isDisabled={disabled}
        />
      ),
    )

    .with({ type: IntakeQuestionType.DURATION }, (v) => (
      <>
        <div className="row wrap font-small">
          <NumberInput
            placeholder="Years"
            bare={true}
            value={v.value?.years ?? null}
            name={name}
            error={hasError ? "Required" : undefined}
            setValue={(newValue) => {
              onChange({
                ...field,
                value: {
                  ...v.value,
                  years: newValue,
                },
              });
            }}
            disabled={disabled}
          />{" "}
          years{" "}
          <NumberInput
            placeholder="Months"
            bare={true}
            value={v.value?.months ?? null}
            name={name}
            error={hasError ? "Required" : undefined}
            setValue={(newValue) => {
              onChange({
                ...field,
                value: {
                  ...v.value,
                  months: newValue,
                },
              });
            }}
            disabled={disabled}
          />{" "}
          months{" "}
          <NumberInput
            placeholder="Days"
            bare={true}
            value={v.value?.days ?? null}
            name={name}
            error={hasError ? "Required" : undefined}
            setValue={(newValue) => {
              onChange({
                ...field,
                value: {
                  ...v.value,
                  days: newValue,
                },
              });
            }}
            disabled={disabled}
          />{" "}
          days
        </div>
        <div className="error font-small">
          {hasError ? "Required" : undefined}
        </div>
      </>
    ))

    .with({ type: IntakeQuestionType.DISPLAY_ONLY }, () => <></>)

    .with({ type: IntakeQuestionType.ADDRESS }, (v) => (
      <IntakeAddressField
        onChange={(newValue) => {
          onChange({
            ...field,
            value: { ...newValue },
          });
        }}
        error={hasError ? "Required" : undefined}
        value={v.value}
        isDisabled={disabled}
      />
    ))

    .with({ type: IntakeQuestionType.REQUEST_OWNER_SELECT }, (v) => (
      <IntakeSubmissionOptionSelect
        fieldType={v.type}
        type="single"
        error={hasError ? "Required" : undefined}
        field={v}
        onChange={(newValue) => {
          onChange({
            ...field,
            value: newValue?.value ?? null,
          });
        }}
        value={v.value}
        disabled={disabled}
      />
    ))

    .with({ type: IntakeQuestionType.DOCUMENTS }, (v) =>
      disabled ? (
        <></>
      ) : vendorUser && forCompanyId ? (
        <VendorPortalDocumentManager
          error={hasError ? "Required" : undefined}
          existingDocs={existingDocs ?? []}
          bare={true}
          className="intake-document-manager"
          onDocsChanged={(docs) => {
            onChange({
              ...v,
              value: docs.map((d) => d.id),
            });
          }}
          forCompanyId={forCompanyId ?? ""}
          forceDocumentTypes={
            v.documentTypes?.length ? v.documentTypes : undefined
          }
        />
      ) : (
        <DocumentManager
          error={hasError ? "Required" : undefined}
          existingDocs={existingDocs ?? []}
          bare={true}
          className="intake-document-manager"
          onDocsChanged={(docs) => {
            onChange({
              ...v,
              value: docs.map((d) => d.id),
            });
          }}
          forceDocumentTypes={
            v.documentTypes?.length ? v.documentTypes : undefined
          }
        />
      ),
    )

    .with(
      { type: "DEPARTMENT_SELECT_V2" },

      (v) => (
        <div>
          <IntakeSubmissionOptionSelect
            fieldType={IntakeQuestionType.DEPARTMENT_SELECT}
            error={
              hasError &&
              (v.isRequired
                ? v.isMulti
                  ? v.value.length === 0
                  : !v.value
                : false)
                ? "Required"
                : undefined
            }
            field={v}
            {...(v.isMulti
              ? {
                  type: "multi",
                  value: v.value.map((d) => d.id),
                  onChange: (newValue) => {
                    onChange({
                      ...field,
                      value: newValue.map((option) => ({
                        id: option.value,
                        name: option.label,
                        departmentApproverAssigneeUserId: null,
                      })),
                    });
                  },
                }
              : {
                  type: "single",
                  value: v.value?.id ?? null,
                  onChange: (newValue) => {
                    onChange({
                      ...field,
                      value: newValue
                        ? {
                            id: newValue.value,
                            name: newValue.label,
                            departmentApproverAssigneeUserId: null,
                          }
                        : null,
                    });
                  },
                })}
            disabled={disabled}
          />
          {v.requireDepartmentApproverAssigneeSelection ? (
            <div>
              {v.isMulti ? (
                v.value.map((department) => (
                  <AsyncMemberSelect
                    key={department.id}
                    label={`Select a department approver (${department.name})`}
                    onChange={(option) => {
                      onChange({
                        ...field,
                        value: v.value.map((d) =>
                          d.id === department.id
                            ? {
                                ...d,
                                departmentApproverAssigneeUserId:
                                  option?.value ?? null,
                              }
                            : d,
                        ),
                      });
                    }}
                    value={department.departmentApproverAssigneeUserId}
                    approverForDepartmentIds={[department.id]}
                    required
                    error={
                      hasError && !department.departmentApproverAssigneeUserId
                        ? "Required"
                        : undefined
                    }
                  />
                ))
              ) : v.value ? (
                <AsyncMemberSelect
                  key={v.value.id}
                  label={`Select a department approver (${v.value.name})`}
                  onChange={(option) => {
                    onChange({
                      ...field,
                      value: {
                        ...v.value,
                        departmentApproverAssigneeUserId: option?.value ?? null,
                      },
                    });
                  }}
                  value={v.value.departmentApproverAssigneeUserId}
                  approverForDepartmentIds={[v.value.id]}
                  required
                  error={
                    hasError && !v.value.departmentApproverAssigneeUserId
                      ? "Required"
                      : undefined
                  }
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ),
    )

    .with(
      { type: IntakeQuestionType.LOCATION_SELECT },
      { type: IntakeQuestionType.MEMBER_SELECT },
      { type: IntakeQuestionType.DEPARTMENT_SELECT },
      { type: IntakeQuestionType.VENDOR_SELECT },
      { type: IntakeQuestionType.SERVICE_SELECT },
      { type: IntakeQuestionType.SPEND_CATEGORY_SELECT },
      { type: IntakeQuestionType.BUSINESS_UNIT_SELECT },
      { type: IntakeQuestionType.CUSTOM_SELECT },
      { type: IntakeQuestionType.ENTITY_SELECT },

      (v) => (
        <IntakeSubmissionOptionSelect
          fieldType={v.type}
          error={hasError ? "Required" : undefined}
          field={v}
          {...(v.isMulti
            ? {
                type: "multi",
                value: v.value,
                onChange: (newValue) => {
                  onChange({
                    ...field,
                    value: newValue.map((o) => o.value),
                  });
                },
                onOptionCreated:
                  v.type === IntakeQuestionType.VENDOR_SELECT
                    ? (option) => {
                        onChange({
                          ...field,
                          value: [...v.value, option.value],
                        });
                      }
                    : undefined,
              }
            : {
                type: "single",
                value: v.value,
                onChange: (newValue) => {
                  onChange({
                    ...field,
                    value: newValue?.value ?? null,
                  });
                },
                onOptionCreated:
                  v.type === IntakeQuestionType.VENDOR_SELECT
                    ? (option) => {
                        onChange({
                          ...field,
                          value: option.value,
                        });
                      }
                    : undefined,
              })}
          disabled={disabled}
        />
      ),
    )

    .with({ type: IntakeQuestionType.NEEDS_REQUEST_SIGNER }, (v) => (
      <div>
        <RadioGroup
          label=""
          options={[
            { label: "Yes", value: "1" },
            { label: "No", value: "0" },
          ]}
          selectedItem={v.value ? "1" : "0"}
          onChange={(newValue) =>
            onChange({
              ...field,
              value: newValue === "1" ? true : false,
            })
          }
          disabled={disabled}
          error={hasError ? "Required" : undefined}
        />
      </div>
    ))
    .exhaustive();
};
